/**
 * The two modes the day panel's head can put the list into: everything folded, and everything
 * selectable.
 *
 * Both are COMPONENT state, deliberately. Neither is a fact about the plan — folding a card changes
 * nothing that is saved, and a selection that is never confirmed leaves no trace — so putting either
 * in the document store would make the plan dirty for looking at it. The WORDS they speak are the
 * model's (`collapseAllLabel`, `supersetModeView`, `ExerciseView.fold`); this holds only the
 * booleans behind them.
 *
 * ── One hook rather than two, because the two are one head ───────────────────────────────────
 * They move together at exactly the moments that matter: stepping to another day drops both, and
 * turning selection on closes nothing but itself. Two hooks would leave the reset written twice, in
 * the component, where the next contributor has to notice both.
 *
 * ── The selection is RECONCILED against the day, every render ────────────────────────────────
 * A ticked card can stop being tickable while the bar is up: the trainer deletes it, links it into
 * somebody else's bracket, or moves it into the warm-up run. The picks were kept regardless until
 * 7 September 2026, so the bar went on reading "Wybrano 2" over one eligible exercise and `Połącz`
 * stayed live — and pressing it called an operation that filtered to one member and returned
 * without touching the tree, which the panel then announced as a join. The eligible tokens arrive
 * as a parameter (the form has the day's cards; this hook has none) and anything else is dropped,
 * adjusted DURING RENDER for the same reason the day step below is.
 *
 * ── The fold is a DEFAULT plus overrides, not a set of folded cards ──────────────────────────
 * "Zwiń wszystkie" flips the default and clears every override, so a card the trainer opened by hand
 * before pressing it folds with the rest — which is what the button says it does. A set of tokens
 * would need every card in the day enumerated to answer the same question, and the day's card list
 * is not this module's to know.
 *
 * ── The default is THREE states, because the warm-up run opens folded (mock, 7 Sept 2026) ────
 * The mock draws a warm-up as one 36px row — icon, name, chevron, ⋯, delete — and the main work
 * below it as full cards. Ours gave each warm-up its own set table and note field, which pushed
 * `PRACA GŁÓWNA` from y≈283 down to y≈676 and put the day's first working set below the fold at
 * 1512×1000.
 *
 * A boolean default cannot express that, and the obvious patch — fold warm-ups always — fights the
 * head's own button: "Rozwiń wszystkie" would leave three cards shut. So the default is
 * `warmupsFolded` on entry, and the button steps between `allFolded` and `allOpen` from there. Its
 * word is read off `isCollapsedAll`, which is true in exactly one of the three.
 *
 * Which cards are warm-ups arrives as a PARAMETER, like the superset's eligible tokens and for the
 * same reason: this hook holds booleans and the form holds the day's cards.
 */

import { useState } from 'react';

/** What a card with no override of its own does. See the header. */
type FoldDefault = 'warmupsFolded' | 'allFolded' | 'allOpen';

export interface DayFoldState {
  /** Whether the default folds EVERY card — the one state the head's button words itself from. */
  readonly isCollapsedAll: boolean;
  readonly isFolded: (exerciseSlotToken: string) => boolean;
  readonly toggle: (exerciseSlotToken: string) => void;
  readonly toggleAll: () => void;
}

export interface DaySupersetState {
  readonly isOn: boolean;
  readonly picks: readonly string[];
  readonly isPicked: (exerciseSlotToken: string) => boolean;
  readonly toggleMode: () => void;
  readonly togglePick: (exerciseSlotToken: string) => void;
  /** Leave the mode with nothing joined — Anuluj, and what a finished join calls too. */
  readonly clear: () => void;
}

export interface DayPanelChrome {
  readonly fold: DayFoldState;
  readonly superset: DaySupersetState;
}

export function useDayPanelChrome(
  daySlotToken: string,
  /** Every exercise of THIS day a superset may still take — see the header. */
  eligibleSupersetTokens: readonly string[],
  /** The day's warm-up run, by slot token. Those cards open folded — see the header. */
  warmupTokens: readonly string[],
): DayPanelChrome {
  const [foldDefault, setFoldDefault] = useState<FoldDefault>('warmupsFolded');
  const [overrides, setOverrides] = useState<Readonly<Record<string, boolean>>>({});
  const [isSupersetOn, setSupersetOn] = useState(false);
  const [picks, setPicks] = useState<readonly string[]>([]);

  // Stepping the dock to another day is a different list of cards, and carrying either mode across
  // would leave a trainer selecting rows they never looked at.
  //
  // Adjusted during render rather than in an effect — React's own documented pattern for state that
  // depends on a prop, and the one the compiler's lint enforces here: a `useEffect` resetting state
  // runs a frame AFTER the new day is already painted, so the trainer would see the previous day's
  // folds on it for one frame.
  const [seenDay, setSeenDay] = useState(daySlotToken);
  const dayChanged = seenDay !== daySlotToken;
  if (dayChanged) {
    setSeenDay(daySlotToken);
    setFoldDefault('warmupsFolded');
    setOverrides({});
    setSupersetOn(false);
    setPicks([]);
  }

  // A tick the day no longer offers is dropped. Convergent, so it cannot loop: the next render's
  // `picks` is already the filtered list and the lengths then match. Skipped on a day step, where
  // the reset above has already emptied the selection and `picks` is one render stale.
  const eligible = new Set(eligibleSupersetTokens);
  const livePicks = dayChanged ? [] : picks.filter((token) => eligible.has(token));
  if (!dayChanged && livePicks.length !== picks.length) setPicks(livePicks);

  const warmups = new Set(warmupTokens);
  const foldsByDefault = (token: string): boolean =>
    foldDefault === 'warmupsFolded' ? warmups.has(token) : foldDefault === 'allFolded';

  return {
    fold: {
      isCollapsedAll: foldDefault === 'allFolded',
      isFolded: (token) => overrides[token] ?? foldsByDefault(token),
      toggle: (token) => {
        setOverrides((current) => ({
          ...current,
          [token]: !(current[token] ?? foldsByDefault(token)),
        }));
      },
      toggleAll: () => {
        // From the entry state the button folds; after that it alternates. Every override goes with
        // it, so a card the trainer opened by hand before pressing folds with the rest — which is
        // what the button says it does.
        setFoldDefault((current) => (current === 'allFolded' ? 'allOpen' : 'allFolded'));
        setOverrides({});
      },
    },
    superset: {
      isOn: isSupersetOn,
      // The reconciled list, not the stored one: the bar reads its count from here, so a pick the
      // day has dropped stops being counted in the SAME render it stops being tickable.
      picks: livePicks,
      isPicked: (token) => livePicks.includes(token),
      toggleMode: () => {
        setSupersetOn((current) => !current);
        setPicks([]);
      },
      togglePick: (token) => {
        setPicks((current) =>
          current.includes(token)
            ? current.filter((candidate) => candidate !== token)
            : [...current, token],
        );
      },
      clear: () => {
        setSupersetOn(false);
        setPicks([]);
      },
    },
  };
}
